import React, { forwardRef, useState, useEffect, useRef, JSX } from 'react';
import { Box } from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import {
  ChatMessage,
  ChatSession,
  ChatMessageUser,
  ChatMessageError,
  ChatMessageStreaming,
  ChatMessageStatus,
} from 'types/types';
import { ConversationHandle } from 'components/Conversation';
import { useAppState, useSelectedCandidate } from 'hooks/GlobalContext';
import { BackstoryTextFieldRef } from 'components/BackstoryTextField';
import { ResumeChatInput } from './ResumeChatInput';
import { ResumeChatMessages } from './ResumeChatMessages';
import { defaultMessage, emptyMetadata } from './resumeChatDefaults';
import { ResumeChatProps } from './resumeChatTypes';

const ResumeChat = forwardRef<ConversationHandle, ResumeChatProps>(
  (props: ResumeChatProps, ref): JSX.Element => {
    const {
      session,
      onResumeChange,
      resume,
      sx,
      // tunables = {
      //   enableRAG: true,
      //   enableTools: true,
      //   enableContext: true,
      //   topK: 50,
      //   temperature: 0.7,
      //   threshold: 0.75,
      // },
    } = props;
    const { apiClient } = useAuth();
    const { selectedCandidate } = useSelectedCandidate();
    const [processingMessage, setProcessingMessage] = useState<
      ChatMessageStatus | ChatMessageError | null
    >(null);
    const [streamingMessage, setStreamingMessage] = useState<ChatMessage | null>(null);
    const backstoryTextRef = useRef<BackstoryTextFieldRef>(null);

    const { setSnack } = useAppState();

    const [chatSession, setChatSession] = useState<ChatSession | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [streaming, setStreaming] = useState<boolean>(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const onDelete = async (session: ChatSession): Promise<void> => {
      if (!session.id) {
        return;
      }
      try {
        await apiClient.resetChatSession(session.id);
        // If we're deleting the currently selected session, clear it
        setMessages([]);
        setSnack('Session reset succeeded', 'success');
      } catch (error) {
        console.error('Failed to delete session:', error);
        setSnack('Failed to delete session', 'error');
      }
    };

    // Send message
    const sendMessage = async (message: string): Promise<void> => {
      if (!message.trim() || !chatSession?.id || streaming || !selectedCandidate) return;

      const messageContent = message;
      setStreaming(true);

      const chatMessage: ChatMessageUser = {
        sessionId: chatSession.id,
        role: 'user',
        content: messageContent,
        status: 'done',
        type: 'text',
        extraContext: { resume: resume },
        timestamp: new Date(),
      };

      setProcessingMessage({
        ...defaultMessage,
        status: 'status',
        activity: 'info',
        content: `Establishing connection with ${selectedCandidate.firstName}'s chat session.`,
      });

      setMessages(prev => {
        return [...prev, chatMessage] as ChatMessage[];
      });

      try {
        apiClient.sendMessageStream(chatMessage, {
          onMessage: (msg: ChatMessage): void => {
            if (msg.content.match(/^ANSWER:\s*/)) {
              msg.content = msg.content.replace(/^ANSWER:\s*/, '');
              msg.extraContext = {
                isAnswer: true,
              };
            } else {
              onResumeChange(message, msg.content);
            }

            setMessages(prev => {
              const filtered = prev.filter(m => m.id !== msg.id);
              return [...filtered, msg] as ChatMessage[];
            });
            setStreamingMessage(null);
            setProcessingMessage(null);
          },
          onError: (error: string | ChatMessageError): void => {
            console.log('onError:', error);
            // Type-guard to determine if this is a ChatMessageBase or a string
            if (typeof error === 'object' && error !== null && 'content' in error) {
              setProcessingMessage(error);
            } else {
              setProcessingMessage({
                ...defaultMessage,
                status: 'error',
                content: error,
              });
            }
            setStreaming(false);
          },
          onStreaming: (chunk: ChatMessageStreaming): void => {
            // console.log("onStreaming:", chunk);
            // Close out any in-progress "thinking" bubble now that the
            // model has moved on to producing its actual response.
            setProcessingMessage(null);
            const prefix = 'ANSWER:';
            let content = chunk.content || '';
            for (let i = prefix.length; i >= 1; i--) {
              if (chunk.content.startsWith(prefix.slice(0, i))) {
                content = chunk.content.slice(i);
                break;
              }
            }
            content = content.trim();
            if (streamingMessage?.content === content) {
              return; // Avoid duplicate updates
            }
            setStreamingMessage({
              ...chunk,
              content,
              role: 'assistant',
              metadata: emptyMetadata,
            });
          },
          onStatus: (status: ChatMessageStatus): void => {
            setProcessingMessage(status);
          },
          onComplete: (): void => {
            console.log('onComplete');
            setStreamingMessage(null);
            setProcessingMessage(null);
            setStreaming(false);
          },
        });
      } catch (error) {
        console.error('Failed to send message:', error);
        setStreaming(false);
      }
    };

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Load sessions when username changes
    useEffect(() => {
      if (!selectedCandidate) {
        setSnack('Candidate not selected', 'error');
        return;
      }
      try {
        setLoading(true);
        apiClient
          .getOrCreateChatSession(selectedCandidate, session, 'edit_resume')
          .then(session => {
            setChatSession(session);
            setLoading(false);
          });
      } catch (error) {
        setSnack('Unable to load chat session', 'error');
      } finally {
        setLoading(false);
      }
    }, [selectedCandidate, apiClient, setSnack]);

    // Load messages when session changes
    useEffect(() => {
      const loadMessages = async (): Promise<void> => {
        if (!chatSession?.id) return;

        try {
          const result = await apiClient.getChatMessages(chatSession.id);
          const chatMessages: ChatMessage[] = result.data;
          console.log(`getChatMessages returned ${chatMessages.length} messages.`, chatMessages);
          chatMessages.forEach(msg => {
            if (msg.role === 'assistant' && msg.content.match(/^ANSWER:\s*/)) {
              msg.content = msg.content.replace(/^ANSWER: */, '');
              msg.extraContext = {
                isAnswer: true,
              };
            }
          });

          setMessages(chatMessages);
          setProcessingMessage(null);
          setStreamingMessage(null);
        } catch (error) {
          console.error('Failed to load messages:', error);
        }
      };

      if (chatSession?.id) {
        loadMessages();
      }
    }, [chatSession, apiClient]);

    return (
      <Box
        className="ResumeChat"
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%' /* Restrict to main-container's height */,
          width: '100%',
          maxWidth: '100%',
          minHeight: 0 /* Prevent flex overflow */,
          maxHeight: 'min-content',
          '& > *:not(.Scrollable)': {
            flexShrink: 0 /* Prevent shrinking */,
          },
          position: 'relative',
          ...sx,
        }}
      >
        {/* Chat Interface */}
        {/* Scrollable Messages Area */}
        {chatSession && (
          <ResumeChatMessages
            chatSession={chatSession}
            messages={messages}
            processingMessage={processingMessage}
            streamingMessage={streamingMessage}
            streaming={streaming}
            messagesEndRef={messagesEndRef}
          />
        )}
        <ResumeChatInput
          chatSession={chatSession}
          textRef={backstoryTextRef}
          streaming={streaming}
          loading={loading}
          onDelete={onDelete}
          onSend={sendMessage}
        />
      </Box>
    );
  }
);
ResumeChat.displayName = 'ResumeChat';
export { ResumeChat };
